import React, { useState } from 'react'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    UniqueIdentifier
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import classNames from 'classnames'

// evertel
import {
    Card, CardHeader, CardBody,
    Row, Col, Button, Icon, Text, ToggleSwitch,
    Tooltip, TooltipTrigger, TooltipContent
} from '@evertel/web/ui'
import { ParsedItem } from '../../schema-builder-dnd/types'

const MAX_TILE_FIELDS = 10
const dragInstructionsId = 'room-tile-fields-instructions'

// a press that lands on a row's own control works that control rather than starting a drag
class ControlAwarePointerSensor extends PointerSensor {
    static activators = [{
        eventName: 'onPointerDown' as const,
        handler: ({ nativeEvent: event }: React.PointerEvent) => {
            if (!event.isPrimary || event.button !== 0) return false

            return !(event.target as HTMLElement)?.closest?.('.toggle-switch, [data-no-drag]')
        }
    }]
}

interface RoomTileFieldsCardProps {
    fields: ParsedItem[]
    /** tile entries whose field the form no longer has - kept, but only removable */
    danglingFields: { id: string, title: string }[]
    orderedIds: string[]
    enabledIds: string[]
    onReorder: (fieldIds: string[]) => void
    onToggleField: (fieldId: string, checked: boolean) => void
    onRemoveField: (fieldId: string) => void
    onRestoreDefaults: () => void
}

const RoomTileFieldsCard: React.FC<RoomTileFieldsCardProps> = ({
    fields,
    danglingFields,
    orderedIds,
    enabledIds,
    onReorder,
    onToggleField,
    onRemoveField,
    onRestoreDefaults
}) => {

    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)

    const sensors = useSensors(
        useSensor(ControlAwarePointerSensor, {
            activationConstraint: {
                distance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id)
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event

        setActiveId(null)

        if (over && active.id !== over.id) {
            const oldIndex = orderedIds.indexOf(active.id as string)
            const newIndex = orderedIds.indexOf(over.id as string)

            if (oldIndex !== -1 && newIndex !== -1) {
                onReorder(arrayMove(orderedIds, oldIndex, newIndex))
            }
        }
    }

    const reachedMax = enabledIds.length >= MAX_TILE_FIELDS

    return (
        <Card className="room-tile-panel">
            <CardHeader>
                <Row
                    align="between"
                    valign="center"
                    className="w-100">
                    <Text
                        tag="h5"
                        bold
                        className="mb-0">
                        {`Field visibility (${enabledIds.length}/${MAX_TILE_FIELDS})`}
                    </Text>
                    <Button
                        color="neutral"
                        onClick={onRestoreDefaults}>
                        Restore defaults
                    </Button>
                </Row>
            </CardHeader>
            <CardBody>
                {(reachedMax) &&
                    <Text
                        tag="p"
                        size={12}
                        color="muted">
                        {`You have selected the maximum of ${MAX_TILE_FIELDS} fields. Turn one off to choose a different one.`}
                    </Text>
                }
                <Row
                    valign="center"
                    className="room-tile-fields-header rounded-top px-3 py-3">
                    <Col xs={2}>
                        <Text bold>
                            Order
                        </Text>
                    </Col>
                    <Col>
                        <Text bold>
                            Field in form
                        </Text>
                    </Col>
                    <Col
                        xs={4}
                        align="right">
                        <Text bold>
                            Show in preview
                        </Text>
                    </Col>
                </Row>
                <div
                    className="visually-hidden"
                    id={dragInstructionsId}>
                    Drag items to reorder the fields shown in the room preview tile.
                </div>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}>
                    <SortableContext
                        items={orderedIds}
                        strategy={verticalListSortingStrategy}>
                        {orderedIds.map((fieldId, index) => {
                            const removed = danglingFields.find(f => f.id === fieldId)
                            const field = fields.find(f => f.id === fieldId)
                            if (!field && !removed) return null

                            const checked = enabledIds.includes(fieldId)

                            return (
                                <FieldRow
                                    key={fieldId}
                                    id={fieldId}
                                    title={(removed) ? removed.title : field.title as string}
                                    required={!removed && !!field.required}
                                    isRemoved={!!removed}
                                    index={index}
                                    activeId={activeId}
                                    checked={checked}
                                    disabled={!checked && reachedMax}
                                    onToggle={(value) => onToggleField(fieldId, value)}
                                    onRemove={() => onRemoveField(fieldId)}
                                />
                            )
                        })}
                    </SortableContext>
                </DndContext>
            </CardBody>
        </Card>
    )
}

interface FieldRowProps {
    id: string
    title: string
    required: boolean
    /** the form no longer has this field: it stays in the tile until it is removed here */
    isRemoved: boolean
    index: number
    activeId: UniqueIdentifier | null
    checked: boolean
    disabled: boolean
    onToggle: (checked: boolean) => void
    onRemove: () => void
}

const FieldRow: React.FC<FieldRowProps> = ({
    id,
    title,
    required,
    isRemoved,
    index,
    activeId,
    checked,
    disabled,
    onToggle,
    onRemove
}) => {

    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id })

    // Only apply transform/transition during active drag
    // This prevents the jump-back-then-animate issue when drag ends
    const style = {
        transform: activeId ? CSS.Transform.toString(transform) : undefined,
        transition: activeId ? transition : undefined,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab'
    }

    // the whole row is the drag activator (the grip is just an affordance), so it carries
    // dnd-kit's attributes. Registering it as the activator node too is what stops a Space
    // press on the toggle from starting a drag
    return (
        <Row
            ref={(node: HTMLDivElement | null) => {
                setNodeRef(node)
                setActivatorNodeRef(node)
            }}
            {...attributes}
            {...listeners}
            style={style}
            valign="center"
            className={classNames('room-tile-field-row border-bottom px-3 py-3', {
                'room-tile-field-row--removed': isRemoved
            })}
            aria-label={`Field ${index + 1}: ${title}${(isRemoved) ? ', removed from the form' : ''}. Drag to reorder`}
            aria-describedby={[attributes['aria-describedby'], dragInstructionsId].filter(Boolean).join(' ')}>
            <Col xs={2}>
                <Icon
                    name="grip-dots-vertical"
                    color="muted"
                    aria-hidden="true"
                />
            </Col>
            {/* one Text wraps the lot: Col is a flex column, so a sibling would drop to its own line */}
            <Col>
                <Text color={(isRemoved) ? 'muted' : undefined}>
                    <Text
                        tag="span"
                        className={(isRemoved) ? 'text-decoration-line-through' : undefined}>
                        {title}
                    </Text>
                    {(required) &&
                        <Text
                            tag="span"
                            color="danger">
                            {' *'}
                        </Text>
                    }
                    {(isRemoved) &&
                        <Tooltip>
                            <TooltipTrigger
                                className="ms-1"
                                data-no-drag>
                                <Icon
                                    name="circle-info"
                                    color="muted"
                                    aria-label="Why this field is still listed"
                                />
                            </TooltipTrigger>
                            <TooltipContent>
                                This field was removed from the form. EverDocs created before it was removed can still
                                show it, so it stays in the preview until you remove it here.
                            </TooltipContent>
                        </Tooltip>
                    }
                </Text>
            </Col>
            <Col
                xs={4}
                align="right">
                {(isRemoved) ?
                    <Button
                        color="transparent"
                        className="me-2"
                        data-no-drag
                        onClick={onRemove}
                        aria-label={`Remove ${title} from the room preview tile`}>
                        <Icon
                            name="circle-xmark"
                            color="danger"
                        />
                    </Button>
                    :
                    <ToggleSwitch
                        checked={checked}
                        disabled={disabled}
                        onChange={onToggle}
                        aria-label={`Show ${title} in the room preview tile`}
                    />
                }
            </Col>
        </Row>
    )
}

export { RoomTileFieldsCard }
